'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import type React from 'react';
import { Suspense } from 'react';
import { FaucetModal } from '@/components/faucet/faucet-modal';
import { FeedbackModal } from '@/components/feedback/feedback-modal';
import { GlobeMount } from '@/components/globe/globe-mount';
import { GraduationHowItWorksModal } from '@/components/how-it-works/graduation-how-it-works-modal';
import { ManipulationHowItWorksModal } from '@/components/how-it-works/manipulation-how-it-works-modal';
import { AppFooter } from '@/components/layout/app-footer';
import { AppHeader } from '@/components/layout/app-header';
import { APP_HEADER_CHROME_PADDING_CLASS } from '@/components/layout/app-header-sticky-top';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { MainnetConstructionModal } from '@/components/layout/mainnet-construction-modal';
import { MobileMenu } from '@/components/layout/mobile-menu';
import { MobileNav } from '@/components/layout/mobile-nav';
import { isSimpleHeaderRoute } from '@/components/layout/simple-header-routes';
import { MarketLifecycleModal } from '@/components/market/lifecycle-modal';
import { CompleteProfileModal } from '@/components/profile/complete-profile-modal';
import { SearchModal } from '@/components/search/search-modal';
import { GlobalShareModal } from '@/components/share/global-share-modal';
import { SimulatedTradeModal } from '@/components/simulated-trade/simulated-trade-modal';
import { LandingMobileCta } from '@/components/support-mainnet/landing-mobile-cta';
import { ManageWalletsModal } from '@/components/wallet/manage-wallets-modal';
import { ViewerPortfolioSnapshotQueryOwner } from '@/hooks/positions/user-portfolio/query-owner';
import { useSearchShortcut } from '@/hooks/ui/use-search-shortcut';
import { stripDemoModePath } from '@/lib/network';
import { CONTENT_MIN_H_CLASS, CONTENT_VERTICAL_CLASS, GUTTER_CLASS, GUTTER_CLASS_NARROW, LANDING_CONTENT_VERTICAL_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';

/** Routes where the full app chrome (header, sidebar, footer, mobile nav) is hidden. */
const CHROMELESS_ROUTES: string[] = ['/onboarding', '/auth/callback', '/workspace/pattern', '/rewards'];
function isChromeless(pathname: string): boolean {
  const bare = stripDemoModePath(pathname);
  return CHROMELESS_ROUTES.some(route => bare === route || bare.startsWith(`${route}/`));
}

/**
 * Routes that opt into the narrower 5xl content column (`GUTTER_CLASS_NARROW`)
 * instead of the default 7xl gutter, after stripping the demo-mode prefix.
 *
 * `NARROW_GUTTER_ROUTES` -- exact path OR any descendant uses the narrow gutter.
 * `NARROW_GUTTER_EXACT_ROUTES` -- only the exact path uses the narrow gutter;
 * sub-routes (e.g. `/proposals/[proposalId]` detail pages) inherit the default
 * 7xl gutter to match the width of other detail screens (MOR-609).
 */
const NARROW_GUTTER_ROUTES: string[] = ['/portfolio'];
const NARROW_GUTTER_EXACT_ROUTES: string[] = [];
function isNarrowGutter(pathname: string): boolean {
  const bare = stripDemoModePath(pathname);
  if (NARROW_GUTTER_ROUTES.some(route => bare === route || bare.startsWith(`${route}/`))) {
    return true;
  }
  return NARROW_GUTTER_EXACT_ROUTES.some(route => bare === route);
}

// The marketing header is ~80px (the h-16 row + its scroll-backdrop feather) —
// far shorter than the full-app chrome that `--app-header-chrome-height` defaults
// to (~140px: banner + header + categories). The landing hero band reserves this
// var for its height, so pin it to the real marketing-header height on the
// landing root: that way the band is correct from first paint instead of
// shrinking ~60px when AppHeader's post-hydration ResizeObserver measures the
// header (the load/refresh repositioning jank).
const LANDING_CHROME_HEIGHT_STYLE = {
  '--app-header-chrome-height': '5rem'
} as React.CSSProperties;
interface AppShellProps {
  children: React.ReactNode;
}
export function AppShell(t0) {
  const $ = _c(47);
  const {
    children
  } = t0;
  useSearchShortcut();
  const pathname = usePathname();
  const chromeless = isChromeless(pathname);
  const simpleHeader = isSimpleHeaderRoute(pathname);
  const gutter = isNarrowGutter(pathname) ? GUTTER_CLASS_NARROW : GUTTER_CLASS;
  const t1 = simpleHeader ? LANDING_CONTENT_VERTICAL_CLASS : CONTENT_VERTICAL_CLASS;
  let t2;
  if ($[0] !== gutter || $[1] !== t1) {
    t2 = cn(gutter, t1, CONTENT_MIN_H_CLASS, "flex flex-1 flex-col");
    $[0] = gutter;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <main className="flex min-h-0 min-w-0 w-full flex-1 flex-col bg-background-secondary"><div className={t2}>{children}</div></main>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mainContent = t3;
  if (chromeless) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ViewerPortfolioSnapshotQueryOwner />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== children) {
      t5 = <main className="flex min-h-0 min-w-0 w-full flex-1 flex-col">{children}</main>;
      $[7] = children;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t10;
    let t11;
    let t12;
    let t13;
    let t6;
    let t7;
    let t8;
    let t9;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ManageWalletsModal />;
      t7 = <FaucetModal />;
      t8 = <FeedbackModal />;
      t9 = <GlobalShareModal />;
      t10 = <MainnetConstructionModal />;
      t11 = <GraduationHowItWorksModal />;
      t12 = <SimulatedTradeModal />;
      t13 = <ManipulationHowItWorksModal />;
      $[9] = t10;
      $[10] = t11;
      $[11] = t12;
      $[12] = t13;
      $[13] = t6;
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
    } else {
      t10 = $[9];
      t11 = $[10];
      t12 = $[11];
      t13 = $[12];
      t6 = $[13];
      t7 = $[14];
      t8 = $[15];
      t9 = $[16];
    }
    let t14;
    if ($[17] !== t5) {
      t14 = <div className="flex min-h-dvh flex-col">{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}</div>;
      $[17] = t5;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    return t14;
  }
  if (simpleHeader) {
    let t4;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ViewerPortfolioSnapshotQueryOwner />;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    let t5;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Suspense fallback={null}><AppHeader /></Suspense>;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    let t6;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <LandingMobileCta />;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t7;
    if ($[22] !== mainContent) {
      t7 = <div className="flex min-h-dvh flex-col" style={LANDING_CHROME_HEIGHT_STYLE}>{t4}{t5}{mainContent}{t6}</div>;
      $[22] = mainContent;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    return t7;
  }
  let t4;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ViewerPortfolioSnapshotQueryOwner />;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Suspense fallback={null}><AppHeader /></Suspense>;
    t6 = cn("flex flex-1", APP_HEADER_CHROME_PADDING_CLASS);
    $[25] = t5;
    $[26] = t6;
  } else {
    t5 = $[25];
    t6 = $[26];
  }
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AppSidebar />;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== mainContent) {
    t8 = <div className={t6}>{mainContent}{t7}</div>;
    $[28] = mainContent;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AppFooter />;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  let t11;
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Suspense fallback={null}><MobileNav /></Suspense>;
    t11 = <MobileMenu />;
    t12 = <SearchModal />;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
  }
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Suspense fallback={null}><GlobeMount /></Suspense>;
    t14 = <ManageWalletsModal />;
    t15 = <FaucetModal />;
    t16 = <FeedbackModal />;
    t17 = <CompleteProfileModal />;
    t18 = <GlobalShareModal />;
    t19 = <MarketLifecycleModal />;
    t20 = <MainnetConstructionModal />;
    t21 = <GraduationHowItWorksModal />;
    t22 = <SimulatedTradeModal />;
    t23 = <ManipulationHowItWorksModal />;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t16 = $[37];
    t17 = $[38];
    t18 = $[39];
    t19 = $[40];
    t20 = $[41];
    t21 = $[42];
    t22 = $[43];
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t8) {
    t24 = <div className="flex min-h-dvh flex-col">{t4}{t5}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[45] = t8;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
}